import { useGetMe } from "../data";

/**
 * Helper hooks for accessing cohort information from user data
 * 
 * These hooks extract cohort-specific data from the /me endpoint,
 * replacing the old PageBaseContext cohort state.
 * 
 * All hooks use React Query under the hood via useGetMe(),
 * providing automatic caching, refetching, and loading states.
 */

/**
 * Get the user's owned cohort (for admins/content managers)
 * 
 * Returns the cohort that the current user owns/manages.
 * Used in admin interfaces to determine which cohort to manage.
 * 
 * @returns Owned cohort object or null if user doesn't own a cohort
 * 
 * @example
 * ```typescript
 * const ownedCohort = useOwnedCohort();
 * if (ownedCohort) {
 *   console.log(`Managing cohort: ${ownedCohort.title}`);
 * }
 * ```
 */
export const useOwnedCohort = () => {
	const { data: user } = useGetMe();
	return user?.ownedCohort ?? null;
};

/**
 * Get the user's membered cohort (primary organization)
 * 
 * Returns the primary cohort/organization that the user belongs to.
 * Used throughout the app to show organization context.
 * 
 * @returns Membered cohort object or null if user isn't in a cohort
 * 
 * @example
 * ```typescript
 * const memberedCohort = useMemberedCohort();
 * if (memberedCohort) {
 *   console.log(`Member of: ${memberedCohort.title}`);
 * }
 * ```
 */
export const useMemberedCohort = () => {
	const { data: user } = useGetMe();
	return user?.memberedCohort ?? null;
};

/**
 * Get the user's organization name
 * 
 * Returns the organization name from the user's membered cohort.
 * Provides a consistent way to display organization info in the UI.
 * 
 * @returns Organization name string or empty string if not available
 * 
 * @example
 * ```typescript
 * const organizationName = useOrganizationName();
 * return <Typography>{organizationName}</Typography>;
 * ```
 */
export const useOrganizationName = () => {
	const { data: user } = useGetMe();
	return user?.organizationName ?? "";
};

/**
 * Get cohort ID from owned cohort
 * 
 * Helper to extract just the cohort ID for API calls.
 * Returns null if user doesn't own a cohort.
 * 
 * @returns Cohort ID number or null
 * 
 * @example
 * ```typescript
 * const cohortId = useOwnedCohortId();
 * const { data } = useGetCohortSettings(cohortId!, { enabled: !!cohortId });
 * ```
 */
export const useOwnedCohortId = () => {
	const ownedCohort = useOwnedCohort();
	return ownedCohort && typeof ownedCohort !== "boolean" ? ownedCohort.id : null;
};

/**
 * Get cohort ID from membered cohort
 * 
 * Helper to extract just the cohort ID for API calls.
 * Returns null if user isn't in a cohort.
 * 
 * @returns Cohort ID number or null
 * 
 * @example
 * ```typescript
 * const cohortId = useMemberedCohortId();
 * const { data } = useCohortMembers(cohortId!, { enabled: !!cohortId });
 * ```
 */
export const useMemberedCohortId = () => {
	const memberedCohort = useMemberedCohort();
	return memberedCohort ? memberedCohort.id : null;
};
